"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import {
  ALL_SET_ID,
  type DocumentSetInfo,
  useDocumentSet,
} from "@/lib/document-set-context";
import CreateDocumentSetModal from "@/components/create-document-set-modal";
import { MacAppStatus } from "@/components/mac-app-status";
import { ChevronIcon, CirclePlusIcon, RefreshIcon } from "@/lib/icons";

const COLLAPSE_KEY = "qscanner.collapsedSets";

function loadCollapsed(): Set<number> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    if (!raw) return new Set();
    const ids = JSON.parse(raw) as unknown;
    if (!Array.isArray(ids)) return new Set();
    return new Set(ids.filter((n): n is number => typeof n === "number"));
  } catch {
    return new Set();
  }
}

function persistCollapsed(ids: Set<number>) {
  localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...ids]));
}

function Chevron({ open }: { open: boolean }) {
  return (
    <ChevronIcon
      className={`w-3 h-3 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
    />
  );
}

function rowClass(active: boolean): string {
  return `flex items-stretch border-l-2 ${
    active
      ? "border-accent bg-accent/15 text-white"
      : "border-transparent text-muted hover:text-foreground hover:bg-surface-light"
  }`;
}

/**
 * Left sidebar: document sets + New set. (Database-only; no sync.)
 */
export default function DocumentSetSidebar() {
  const { user } = useAuth();
  const { sets, current, loading, selectSet, refresh } = useDocumentSet();
  const router = useRouter();
  const pathname = usePathname();
  const [createOpen, setCreateOpen] = useState(false);
  const [createParentId, setCreateParentId] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [collapseReady, setCollapseReady] = useState(false);

  const childrenByParent = useMemo(() => {
    const map = new Map<number, DocumentSetInfo[]>();
    for (const set of sets) {
      if (set.parentId == null) continue;
      const list = map.get(set.parentId) ?? [];
      list.push(set);
      map.set(set.parentId, list);
    }
    return map;
  }, [sets]);

  const roots = useMemo(
    () => sets.filter((s) => s.parentId == null),
    [sets],
  );

  const selectedParentId = useMemo(() => {
    const selected = sets.find((s) => String(s.id) === current);
    return selected?.parentId ?? null;
  }, [sets, current]);

  useEffect(() => {
    setCollapsed(loadCollapsed());
    setCollapseReady(true);
  }, []);

  useEffect(() => {
    if (!collapseReady) return;
    persistCollapsed(collapsed);
  }, [collapsed, collapseReady]);

  if (!user || user.mustChangePassword) return null;
  if (pathname === "/") return null;

  function isOpen(id: number): boolean {
    // Keep the selected child's parent visible so the highlight isn't hidden.
    if (selectedParentId === id) return true;
    return !collapsed.has(id);
  }

  function toggle(id: number) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function expand(id: number) {
    setCollapsed((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  async function onSelect(id: number) {
    const idStr = String(id);
    if (idStr === current && pathname.startsWith("/documents")) {
      return;
    }
    try {
      if (idStr !== current) {
        await selectSet(id);
      }
      window.location.href = "/documents";
    } catch {
      router.push("/documents");
    }
  }

  function renderRow(
    set: DocumentSetInfo,
    opts: { child?: boolean; kids?: DocumentSetInfo[] },
  ) {
    const active = current === String(set.id);
    const isAllSet = set.id === ALL_SET_ID;
    const kids = opts.kids ?? [];
    const hasKids = kids.length > 0;
    const open = hasKids && isOpen(set.id);
    const canNest = Boolean(set.isGroup) && !isAllSet && !opts.child;

    return (
      <div className={rowClass(active)}>
        {hasKids ? (
          <button
            type="button"
            onClick={() => toggle(set.id)}
            className="shrink-0 pl-2 pr-0.5 text-muted hover:text-accent"
            aria-expanded={open}
            aria-controls={`set-children-${set.id}`}
            aria-label={open ? `Collapse ${set.name}` : `Expand ${set.name}`}
            title={open ? `Collapse ${set.name}` : `Expand ${set.name}`}
          >
            <Chevron open={open} />
          </button>
        ) : (
          <span className={opts.child ? "w-7 shrink-0" : "w-2 shrink-0"} />
        )}
        <button
          type="button"
          onClick={() => void onSelect(set.id)}
          title={
            isAllSet
              ? "Every document across all your sets (read-only — no uploads)"
              : set.path || set.name
          }
          className={`min-w-0 flex-1 text-left py-2 pr-1 text-sm transition-colors ${
            opts.child || hasKids ? "pl-1" : "pl-2"
          }`}
        >
          <div className="font-medium truncate">{set.name}</div>
        </button>
        {canNest && (
          <button
            type="button"
            onClick={() => {
              expand(set.id);
              setCreateParentId(set.id);
              setCreateOpen(true);
            }}
            className="shrink-0 px-2 text-muted hover:text-accent"
            title={`New group under ${set.name}`}
            aria-label={`New group under ${set.name}`}
          >
            <CirclePlusIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <aside
        className="fixed top-[50px] bottom-[50px] left-0 z-30 w-56 flex flex-col border-r border-border bg-surface"
        aria-label="Document sets"
      >
        <div className="flex items-center justify-between gap-2 px-3 py-3 border-b border-border shrink-0">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted m-0">
            Document sets
          </h2>
          <div className="flex items-center gap-1">
            {loading && <span className="text-[10px] text-muted">…</span>}
            <button
              type="button"
              onClick={() => {
                setCreateParentId(null);
                setCreateOpen(true);
              }}
              className="p-0.5"
              title="New set"
              aria-label="New set"
            >
              <CirclePlusIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => void refresh()}
              className="text-muted hover:text-accent p-0.5"
              title="Refresh set list"
              aria-label="Refresh set list"
            >
              <RefreshIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-2 min-h-0">
          {loading && sets.length === 0 && (
            <p className="px-3 text-xs text-muted m-0">Looking for sets…</p>
          )}

          {!loading && sets.length === 0 && (
            <p className="px-3 text-xs text-muted m-0 leading-relaxed">
              No sets yet. Use the add button above.
            </p>
          )}

          {sets.length > 0 && (
            <ul className="list-none p-0 m-0 space-y-0.5">
              {roots.map((set) => {
                const isAllSet = set.id === ALL_SET_ID;
                const kids = childrenByParent.get(set.id) ?? [];
                const open = kids.length > 0 && isOpen(set.id);
                return (
                  <li
                    key={set.id}
                    className={isAllSet ? "mb-1 pb-1 border-b border-border" : undefined}
                  >
                    {renderRow(set, { kids })}
                    {open && (
                      <ul
                        id={`set-children-${set.id}`}
                        className="list-none p-0 m-0"
                      >
                        {kids.map((child) => (
                          <li key={child.id}>{renderRow(child, { child: true })}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="border-t border-border px-3 py-2 shrink-0 bg-surface">
          <MacAppStatus />
        </div>
      </aside>

      <CreateDocumentSetModal
        open={createOpen}
        parentId={createParentId}
        onClose={() => {
          setCreateOpen(false);
          setCreateParentId(null);
        }}
      />
    </>
  );
}
